import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PageLayout } from '@/components';
import { Users, Mic, FileText, Search, Sparkles, Building2, ArrowRight } from 'lucide-react';

const About: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="pt-20 pb-12 sm:pt-24">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <p className="text-blue-600 font-semibold mb-4 tracking-wide uppercase text-sm">
            {t('about.badge')}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight text-slate-900">
            {t('about.heroTitle')}<br />
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              {t('about.heroTitleHighlight')}
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            {t('about.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white/80">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                {t('about.missionTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                {t('about.missionParagraph1')}
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('about.missionParagraph2')}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Mic className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{t('about.missionStepTalkTitle')}</h3>
                    <p className="text-slate-600 text-sm">{t('about.missionStepTalkDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{t('about.missionStepStructureTitle')}</h3>
                    <p className="text-slate-600 text-sm">{t('about.missionStepStructureDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{t('about.missionStepShipTitle')}</h3>
                    <p className="text-slate-600 text-sm">{t('about.missionStepShipDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Breaking Down Walls Section */}
      <section className="py-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              {t('about.eaForEveryoneTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('about.eaForEveryoneSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
              <div className="p-3 bg-emerald-100 rounded-xl w-fit mb-6">
                <Building2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">{t('about.enterpriseTeamsTitle')}</h3>
              <p className="text-slate-600">
                {t('about.enterpriseTeamsDesc')}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
              <div className="p-3 bg-blue-100 rounded-xl w-fit mb-6">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">{t('about.midSizeTeamsTitle')}</h3>
              <p className="text-slate-600">
                {t('about.midSizeTeamsDesc')}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
              <div className="p-3 bg-amber-100 rounded-xl w-fit mb-6">
                <Search className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">{t('about.soloFoundersTitle')}</h3>
              <p className="text-slate-600">
                {t('about.soloFoundersDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-white/80">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              {t('about.storyTitle')}
            </h2>

            <div className="prose prose-lg prose-slate mx-auto">
              <p className="text-slate-600 leading-relaxed mb-6">
                {t('about.storyParagraph1')}
              </p>

              <p className="text-slate-600 leading-relaxed mb-6">
                {t('about.storyParagraph2')}
              </p>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100 my-8">
                <p className="text-xl text-slate-700 font-medium italic text-center mb-0">
                  "{t('about.storyQuote')}"
                </p>
              </div>

              <p className="text-slate-600 leading-relaxed mb-6">
                {t('about.storyParagraph3')}
              </p>

              <div className="text-center mt-10">
                <p className="text-2xl font-bold text-slate-900 mb-2">
                  {t('about.storyTagline1')}
                </p>
                <p className="text-2xl font-bold text-slate-900 mb-2">
                  {t('about.storyTagline2')}
                </p>
                <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  {t('about.storyTagline3')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('about.ctaTitle')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('about.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow-lg"
            >
              {t('about.ctaStartFreeTrial')}
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/free-trial"
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500/20 text-white rounded-xl font-semibold hover:bg-blue-500/30 transition-colors border border-blue-400/30"
            >
              {t('about.ctaRequestDemo')}
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default About;
